/**
 * broker-colors.ts — the shared model for the broker brand-colour editor + preview
 * (Wave W4 — branding, Story: ui+preview).
 *
 * A Mandant's customer-facing surfaces (transactional customer emails + the customer
 * portal) carry the BROKER's OWN three colours. This module is the web-side companion
 * to @brokly/shared's canonical broker-branding shape: the three field definitions
 * (label + one-line help), the Brokly defaults each unset colour previews as, the FIXED
 * readability tokens a broker can never override, and a contrast picker mirroring the
 * api email-template's readableInkOn so the preview matches the sent mail exactly.
 *
 * Colours are validated #RRGGBB (shared isHexColor / HEX_COLOR_PATTERN). Body text,
 * card/page background and borders are FIXED — a broker's brand can never make an email
 * or the portal unreadable.
 *
 * [Source: docs/design/2026-07-29_mandant-config-catalog.md → "Build plan + specs" → W4]
 * [Source: packages/shared/src/broker-branding.ts] (canonical colour keys + hex guard)
 * [Source: apps/api/src/mailer/email-template.ts] (readableInkOn + FIXED email tokens)
 */

import type { BrokerBrandingColorKey, BrokerBrandingColors } from '@brokly/shared';

/**
 * The three broker-settable colours, in canonical order, each with the German label
 * + one-line "was macht das?" help the wizard/Administration surfaces render.
 */
export const BROKER_COLOR_FIELDS: ReadonlyArray<{
  key: BrokerBrandingColorKey;
  label: string;
  help: string;
}> = [
  {
    key: 'primaryColor',
    label: 'Primärfarbe',
    help: 'Buttons, Links, aktive Elemente und Akzente.',
  },
  {
    key: 'secondaryColor',
    label: 'Sekundärfarbe',
    help: 'Sekundäre Buttons, Badges und Hervorhebungen.',
  },
  {
    key: 'headerColor',
    label: 'Kopfzeilenfarbe',
    help: 'Farbband hinter dem Logo (E-Mail-Kopf und Portal-Leiste).',
  },
] as const;

/**
 * The Brokly default each colour degrades to when unset/invalid — so an untouched
 * field still renders a meaningful preview (and matches the sent-mail fallback).
 */
export const BROKER_COLOR_DEFAULTS: Record<BrokerBrandingColorKey, string> = {
  primaryColor: '#3b80e5', // brand blue — the CTA fill
  secondaryColor: '#1b325a', // navy — accent trim
  headerColor: '#1b325a', // navy — the coloured band behind the logo
};

/**
 * FIXED readability tokens (email + portal) — NEVER broker-settable. Mirrors the
 * api email-template's COLOR constants so the client preview equals the sent mail.
 */
export const FIXED_TOKENS = {
  pageBg: '#f7f7f8',
  card: '#ffffff',
  navy: '#1b325a',
  ink: '#111827',
  muted: '#6b7280',
  border: '#e4e4e7',
  white: '#ffffff',
} as const;

/** The standing legal line the email footer carries (mirrors LEGAL_LINE). */
export const PREVIEW_LEGAL_LINE =
  'Automatisierte Broker-Software · Diese E-Mail wurde automatisch versendet.';

/**
 * Pick a readable text colour (white or dark ink) to sit ON a #RRGGBB background —
 * mirrors the api email-template readableInkOn (sRGB relative-luminance, threshold
 * 0.4). Guarantees a header band / CTA label / badge stays legible on whatever
 * colour the broker chose. A non-hex input falls back to white.
 */
export function readableInkOn(hex: string): string {
  const m = /^#([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})$/.exec(hex);
  if (!m) return FIXED_TOKENS.white;
  const toLin = (channel: string): number => {
    const c = parseInt(channel, 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  const luminance = 0.2126 * toLin(m[1]!) + 0.7152 * toLin(m[2]!) + 0.0722 * toLin(m[3]!);
  return luminance > 0.4 ? FIXED_TOKENS.ink : FIXED_TOKENS.white;
}

/**
 * Normalise a user-typed hex toward #RRGGBB: trim, prepend a missing `#`, lowercase.
 * Purely cosmetic — validity is still decided by isHexColor (a partial value stays
 * partial, so the field can show it as invalid without fighting the keystroke).
 */
export function normalizeHex(input: string): string {
  const trimmed = input.trim();
  const withHash = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
  return withHash.toLowerCase();
}

/**
 * Resolve the three editor colours into concrete hex values for the preview:
 * a valid broker colour wins, else the Brokly default for that slot. Never returns
 * null — the preview always renders something meaningful.
 */
export function resolvePreviewColors(colors: BrokerBrandingColors): Record<
  BrokerBrandingColorKey,
  string
> {
  const HEX = /^#[0-9a-fA-F]{6}$/;
  const pick = (key: BrokerBrandingColorKey): string => {
    const value = colors[key];
    return typeof value === 'string' && HEX.test(value)
      ? value.toLowerCase()
      : BROKER_COLOR_DEFAULTS[key];
  };
  return {
    primaryColor: pick('primaryColor'),
    secondaryColor: pick('secondaryColor'),
    headerColor: pick('headerColor'),
  };
}
